import { useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";

const ANNUAL_RETURN = 0.12;

function formatINR(value) {
  if (value >= 1e7) return `₹${(value / 1e7).toFixed(2)} Cr`;
  if (value >= 1e5) return `₹${(value / 1e5).toFixed(1)} L`;
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
}

export default function GrowthSimulator() {
  const [monthly, setMonthly] = useState(15000);
  const [years, setYears] = useState(15);

  const { invested, corpus, points } = useMemo(() => {
    const rate = ANNUAL_RETURN / 12;
    let balance = 0;
    const pts = [];
    for (let y = 1; y <= years; y += 1) {
      for (let m = 0; m < 12; m += 1) balance = balance * (1 + rate) + monthly;
      pts.push(balance);
    }
    return { invested: monthly * 12 * years, corpus: balance, points: pts };
  }, [monthly, years]);

  const max = Math.max(...points, 1);
  const line = points.map((v, i) => `${(i / Math.max(points.length - 1, 1)) * 100},${40 - (v / max) * 36}`).join(" ");

  return (
    <section className="simulator-section" data-testid="investment-simulator">
      <div className="simulator-inner section-wrap">
        <div className="sim-copy">
          <p className="eyebrow light-eyebrow">WATCH IT GROW</p>
          <h2>Small steps.<br /><em>Serious distance.</em></h2>
          <p>Move the sliders and see how a disciplined monthly investment could compound at an assumed 12% p.a. Illustration only — markets move, plans adapt.</p>
          <div className="sim-controls">
            <label data-testid="sim-amount-label">Monthly investment <strong>{formatINR(monthly)}</strong></label>
            <input type="range" min="1000" max="100000" step="1000" value={monthly} onChange={(e) => setMonthly(Number(e.target.value))} data-testid="sim-amount-slider" />
            <label data-testid="sim-years-label">Time horizon <strong>{years} years</strong></label>
            <input type="range" min="1" max="30" step="1" value={years} onChange={(e) => setYears(Number(e.target.value))} data-testid="sim-years-slider" />
          </div>
        </div>
        <div className="sim-panel" data-testid="sim-results">
          <div className="sim-chart-wrap">
            <svg viewBox="0 0 100 44" preserveAspectRatio="none" className="sim-chart" data-testid="sim-growth-chart">
              <polygon points={`0,44 ${line} 100,44`} className="sim-chart-fill" />
              <polyline points={line} className="sim-chart-line" />
            </svg>
          </div>
          <div className="sim-numbers">
            <div><span>You invest</span><strong data-testid="sim-invested">{formatINR(invested)}</strong></div>
            <div className="sim-corpus"><span><TrendingUp size={13} /> Could grow to</span><strong data-testid="sim-corpus-value">{formatINR(corpus)}</strong></div>
          </div>
          <p className="sim-note">Assumes {Math.round(ANNUAL_RETURN * 100)}% p.a. compounded monthly. Not a guarantee of returns.</p>
        </div>
      </div>
    </section>
  );
}
